// src/components/booking/bookingShared.ts
// Piezas puras compartidas entre BookingModal (ficha de vehículo) y AgendarForm
// (link general /agendar). Extraídas SIN cambiar su comportamiento: son las mismas
// funciones/constantes/tipos que vivían dentro de BookingModal.tsx.

import { isBrasil, isUsa } from '@/lib/country';

/* ========================= tipos ========================= */
export type RGB = { r: number; g: number; b: number };
export type DealershipItem = {
  id: number;
  address: string;
  timezone?: string | null;
  /** `dealerships.appointment_comms` crudo; leer SOLO vía `avisoPorCorreoActivo`. */
  appointment_comms?: unknown;
};
export type Slot = { slot_start: string; slot_end: string; capacity: number };

/* ========================= helpers de color (idéntico al botón Agendar) ========================= */
/** Mismo origen de color que usa el botón "Agendar":
 *  theme_color → accent_color → brand.primary_color → brand_color → ui_color
 */
export function pickAccentFromClient(c?: any): string | undefined {
  if (!c) return undefined;
  return (
    c?.theme_color ||
    c?.accent_color ||
    c?.brand?.primary_color ||
    c?.brand_color ||
    c?.ui_color ||
    undefined
  );
}

export function hexToRgb(hex?: string): RGB | null {
  if (!hex) return null;
  const s = hex.replace('#', '').trim();
  const m = s.match(/^([0-9a-f]{3}|[0-9a-f]{6})$/i);
  if (!m) return null;
  let h = m[1].toLowerCase();
  if (h.length === 3) h = h.split('').map((c) => c + c).join('');
  const n = parseInt(h, 16);
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
}

/** Fallback neutral (no morado) si el backend no trae color */
export const FALLBACK_RGB: RGB = { r: 14, g: 165, b: 233 }; // #0EA5E9

export const rgb = (c: RGB | null) =>
  c ? `rgb(${c.r},${c.g},${c.b})` : `rgb(${FALLBACK_RGB.r},${FALLBACK_RGB.g},${FALLBACK_RGB.b})`;

export const rgba = (c: RGB | null, a = 1) =>
  c ? `rgba(${c.r},${c.g},${c.b},${a})` : `rgba(${FALLBACK_RGB.r},${FALLBACK_RGB.g},${FALLBACK_RGB.b},${a})`;

// Calculate relative luminance and return contrasting foreground color
export const getForeground = (c: RGB | null): string => {
  if (!c) return '#fff';
  // Calculate relative luminance (simplified)
  const luminance = (0.299 * c.r + 0.587 * c.g + 0.114 * c.b) / 255;
  return luminance > 0.5 ? '#000' : '#fff';
};

/* ========================= utils ========================= */
export const labelFromTimestamp = (ts: unknown): string => {
  if (!ts) return '';
  if (typeof ts === 'string') {
    const s = ts.replace(' ', 'T');
    const m = s.match(/(?:T| )(\d{2}:\d{2})(?::\d{2})?/);
    if (m) return m[1];
    const m2 = s.match(/\b([01]\d|2[0-3]):([0-5]\d)\b/);
    return m2 ? `${m2[1]}:${m2[2]}` : '';
  }
  if (ts instanceof Date) {
    const m = ts.toISOString().match(/T(\d{2}:\d{2})(?::\d{2})?/);
    return m ? m[1] : '';
  }
  if (typeof ts === 'number') {
    const m = new Date(ts).toISOString().match(/T(\d{2}:\d{2})(?::\d{2})?/);
    return m ? m[1] : '';
  }
  return '';
};

export const minuteKey = (ts: unknown): string => {
  const s =
    typeof ts === 'string' ? ts.replace(' ', 'T')
      : ts instanceof Date ? ts.toISOString()
        : String(ts || '');
  const m = s.match(/^(\d{4}-\d{2}-\d{2})[T ](\d{2}:\d{2})/);
  return m ? `${m[1]}T${m[2]}` : '';
};

/* ========================= zona horaria del NEGOCIO ========================= */
/**
 * La agenda es de la sucursal, no de quien la mira.
 * ============================================================================
 *
 * Hasta acá el sitio mandaba `Intl.DateTimeFormat().resolvedOptions().timeZone`
 * —la zona del NAVEGADOR del visitante— como `p_timezone` a las tres RPC de
 * agendamiento. Con eso, una automotora chilena visitada desde Madrid o Tokio
 * recibía la grilla del DÍA ANTERIOR: `fn_compute_availability` ancla el día con
 * `date_trunc('day', <timestamptz>)`, y para cualquier zona de desfase POSITIVO
 * la medianoche local cae el día previo en UTC. Verificado sobre Postgres para
 * los 365 días de 2026: Europe/Madrid y Asia/Tokyo corren el día SIEMPRE; todas
 * las zonas americanas, NUNCA.
 *
 * El visitante no se enteraba: la fecha la pinta el front desde su propia
 * selección y la hora sale de un regex sobre el string, así que la grilla se ve
 * igual. La cita quedaba guardada un día antes, sin error — o, si el día elegido
 * era mañana, `fn_appointment_book` la rechazaba con `slot_in_past` y el
 * visitante leía "ese horario ya no está disponible" sin entender por qué.
 *
 * La zona correcta es la del NEGOCIO. Precedencia:
 *   1. `dealerships.timezone` — la columna real (migración de B2). Se lee con
 *      tolerancia: si todavía no está aplicada, el select cae a `id,address` y
 *      esto pasa al paso 2 sin romper nada.
 *   2. `clients.country` — mapa igual al que ya usa la base en
 *      `run_calendar_event_reminders`. `country` es NOT NULL DEFAULT 'CL' en
 *      producción, así que siempre resuelve.
 *   3. Chile, que es de donde son casi todas las marcas.
 *
 * Para los tenants actuales (todos CL) el valor que se manda es el MISMO que
 * antes mandaba un visitante chileno: cero cambio en la hora visible y cero
 * cambio en los datos.
 */
export const DEFAULT_BUSINESS_TZ = 'America/Santiago';

/** Mismo mapa que `run_calendar_event_reminders` en la base. */
const COUNTRY_TZ: Record<string, string> = {
  CL: 'America/Santiago',
  BR: 'America/Sao_Paulo',
  AR: 'America/Argentina/Buenos_Aires',
  PE: 'America/Lima',
  CO: 'America/Bogota',
  MX: 'America/Mexico_City',
  // EE.UU. tiene seis zonas: Eastern es solo el default; manda la de la sucursal.
  US: 'America/New_York',
};

export function businessTimezone(
  client?: { country?: string | null } | null,
  dealership?: { timezone?: string | null } | null,
): string {
  const fromBranch = dealership?.timezone?.trim();
  if (fromBranch) return fromBranch;
  const code = client?.country?.trim().toUpperCase();
  return (code && COUNTRY_TZ[code]) || DEFAULT_BUSINESS_TZ;
}

/**
 * Reloj de pared de una zona, sin depender del navegador.
 *
 * `Intl` con `timeZone` explícito es la única forma de leer "qué hora es AHÍ"
 * sin construir Dates intermedios. `hourCycle: 'h23'` evita el `24:xx` que
 * algunos motores devuelven a medianoche. Si la zona no es válida, `Intl` tira:
 * se cae al reloj local, que es lo que había antes de este cambio.
 */
const wallClockIn = (tz: string, at: Date = new Date()) => {
  try {
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: tz, hourCycle: 'h23',
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit',
    }).formatToParts(at).reduce<Record<string, string>>((acc, p) => {
      if (p.type !== 'literal') acc[p.type] = p.value;
      return acc;
    }, {});
    return {
      ymd: `${parts.year}-${parts.month}-${parts.day}`,
      minutes: Number(parts.hour) * 60 + Number(parts.minute),
    };
  } catch {
    const d = at;
    const ymd = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    return { ymd, minutes: d.getHours() * 60 + d.getMinutes() };
  }
};

/** 'YYYY-MM-DD' de HOY en la zona del negocio. */
export const todayYMDIn = (tz: string, at?: Date) => wallClockIn(tz, at).ymd;

/** Minutos desde medianoche de AHORA en la zona del negocio. */
export const nowMinutesIn = (tz: string, at?: Date) => wallClockIn(tz, at).minutes;

export const todayYMD = () => {
  const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

/**
 * `locale` es opcional: sin él (`undefined`) es el idioma del navegador, que es
 * lo de siempre. Solo Brasil ('pt-BR') y EE.UU. ('en-US') pasan uno (ver `bookingDateLocale`).
 */
export const prettyYMD = (ymd?: string, locale?: string) => {
  if (!ymd) return '';
  const [y, m, d] = ymd.split('-').map(Number);
  return new Date(y!, (m ?? 1) - 1, d ?? 1).toLocaleDateString(locale);
};

/**
 * Locale de las fechas de la agenda según el PAÍS del tenant.
 *
 * Brasil → 'pt-BR'; EE.UU. → 'en-US' ("10/21/2026", "October 2026"). Cualquier
 * otro (CL, null, desconocido) → `undefined`, que es exactamente lo de hoy
 * (`toLocaleDateString()` sin locale = el del navegador).
 * Nunca por moneda ni por default_language.
 */
export const bookingDateLocale = (country?: string | null): string | undefined =>
  (isBrasil(country) ? 'pt-BR' : isUsa(country) ? 'en-US' : undefined);

/**
 * Completa `{{var}}` en un texto ya traducido. El `t` de
 * `@/i18n/hooks/useTranslation` solo recibe la clave (sin opciones) e i18next
 * deja los `{{var}}` tal cual cuando no le pasan datos: acá se rellenan.
 */
export const fill = (template: string, vars: Record<string, string | number>): string =>
  template.replace(/\{\{\s*(\w+)\s*\}\}/g, (match, k: string) =>
    (Object.prototype.hasOwnProperty.call(vars, k) ? String(vars[k]) : match));

export const toYMD = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

export const addDays = (d: Date, n: number) => { const c = new Date(d); c.setDate(c.getDate() + n); return c; };

export const startOfMonth = (d: Date) => new Date(d.getFullYear(), d.getMonth(), 1);

export const endOfMonth = (d: Date) => new Date(d.getFullYear(), d.getMonth() + 1, 0);

export const hhmmToMinutes = (hhmm: string) => { const m = hhmm.match(/(\d{2}):(\d{2})/); return m ? (+m[1]) * 60 + (+m[2]) : 0; };

export const nowMinutesLocal = () => { const d = new Date(); return d.getHours() * 60 + d.getMinutes(); };

/**
 * ¿El cupo todavía no pasó?
 *
 * `slot_start` es hora de pared de la sucursal, así que la comparación tiene que
 * ser contra el reloj de la SUCURSAL — hora de pared contra hora de pared, que
 * es exactamente lo que hace la base (`slot_start AT TIME ZONE 'UTC'` contra
 * `now() AT TIME ZONE <zona>`). Con el reloj del visitante, alguien en Madrid a
 * las 20:00 (14:00 en Chile) veía escondida toda la tarde chilena, y el
 * calendario mensual le marcaba días enteros como "sin horarios".
 *
 * `tz` tiene default para no romper llamadores viejos, pero todos los de la
 * agenda lo pasan.
 */
export const isFutureSlotSameDay = (
  ymd: string, slotStart: string, tz: string = DEFAULT_BUSINESS_TZ, at?: Date,
) => {
  const t = todayYMDIn(tz, at); if (ymd > t) return true; if (ymd < t) return false;
  return hhmmToMinutes(labelFromTimestamp(slotStart)) > nowMinutesIn(tz, at);
};

/* ========================= validadores ========================= */
export const isEmail = (e: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e.trim());
export const isNonEmpty = (s: string) => s.trim().length > 1;
export const cleanPhone = (p: string) => p.replace(/\D/g, '');
export const isNumeric = (v: unknown) => typeof v === 'number' || /^\d+$/.test(String(v ?? ''));

/* ========================= constantes ========================= */
/**
 * Motivos de visita para el link general `/agendar` (sin vehículo).
 * Lista FIJA e igual para todos los tenants — en modo ficha el motivo es
 * implícito ('visita showroom') y este selector no se muestra.
 *
 * El ORDEN es el orden visual de los botones: los de arriba son los de mayor
 * volumen. Los nuevos van al final.
 *
 * El valor viaja tal cual a `goauto_sched.appointments.service_name` y el filtro
 * de la agenda del admin compara por igualdad EXACTA de string: agregar motivos
 * es inofensivo, pero renombrar uno existente parte el histórico en dos.
 */
export const VISIT_REASONS = [
  'Ver un vehículo',
  'Tasar mi auto',
  'Financiamiento',
  'Otra consulta',
  'Consignar mi auto',
  'Vender mi auto',
] as const;

/**
 * Clave de traducción (`booking.reasons.<clave>`) de cada motivo, SOLO para la
 * etiqueta visible. El VALOR que se guarda y se compara sigue siendo el string en
 * español de `VISIT_REASONS` (y 'visita showroom' / 'visita' que manda la ficha):
 * eso no se traduce nunca. En es/booking.ts cada etiqueta es idéntica al valor.
 */
const SERVICE_NAME_KEYS: Record<string, string> = {
  'Ver un vehículo': 'verVehiculo',
  'Tasar mi auto': 'tasarAuto',
  'Financiamiento': 'financiamiento',
  'Otra consulta': 'otraConsulta',
  'Consignar mi auto': 'consignarAuto',
  'Vender mi auto': 'venderAuto',
  'visita showroom': 'visitaShowroom',
  'visita': 'visita',
};

/**
 * Etiqueta visible de un motivo / `service_name`. Lo conocido pasa por `t` (en
 * español devuelve el mismo string); lo desconocido (motivos que no son de esta
 * lista, null) se muestra tal cual, como siempre.
 */
export const serviceNameLabel = (
  value: string | null | undefined,
  t: (key: string) => string,
): string => {
  if (!value) return value ?? '';
  const key = Object.prototype.hasOwnProperty.call(SERVICE_NAME_KEYS, value) ? SERVICE_NAME_KEYS[value] : undefined;
  return key ? t(`booking.reasons.${key}`) : value;
};

/* ========================= B4: el cupo como INSTANTE ========================= */
/**
 * Hasta acá un cupo era un string del que se sacaban los dígitos con un regex
 * —`labelFromTimestamp`— porque la base guardaba RELOJ DE PARED etiquetado UTC.
 * Funcionaba, con tres agujeros que el string no puede tapar:
 *
 *   · DST. El desfase de una sucursal no es constante.
 *   · Sucursales en husos distintos. Dos cupos "de las 10:00" en Santiago y en
 *     Isla de Pascua NO son el mismo momento.
 *   · "¿Ya pasó?". Sólo se responde comparando instantes.
 *
 * Desde `20260826120000` la base ofrece las RPC `_utc`, que devuelven el
 * instante real MÁS la zona de la sucursal. Estos helpers pintan eso.
 *
 * Todo acepta las dos formas y prefiere el instante: si la base todavía no
 * tiene las funciones nuevas, `loadAvailability` cae sola a las viejas y estos
 * helpers vuelven al camino del regex. Un solo componente sirve para las dos.
 */

export type SlotUtc = Slot & {
  slot_start_utc?: string | null;
  slot_end_utc?: string | null;
  /** Zona de la sucursal que devolvió la propia RPC. */
  tz?: string | null;
};

const zoneParts = (iso: string, tz: string) => {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  try {
    const f = new Intl.DateTimeFormat('en-CA', {
      timeZone: tz, hourCycle: 'h23',
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit',
    });
    const p: Record<string, string> = {};
    for (const part of f.formatToParts(d)) p[part.type] = part.value;
    return { ymd: `${p.year}-${p.month}-${p.day}`, hhmm: `${p.hour}:${p.minute}` };
  } catch {
    return { ymd: d.toISOString().slice(0, 10), hhmm: d.toISOString().slice(11, 16) };
  }
};

/** Instante real → `'10:00'` en la zona del negocio. */
export const labelFromInstant = (iso: string, tz: string) => zoneParts(iso, tz)?.hhmm ?? '';

/** Instante real → `'2026-09-07'` en la zona del negocio. */
export const ymdFromInstant = (iso: string, tz: string) => zoneParts(iso, tz)?.ymd ?? '';

/** La etiqueta de un cupo, venga como instante o como reloj de pared. */
export const slotStartLabel = (s: SlotUtc, tz: string) =>
  (s.slot_start_utc ? labelFromInstant(s.slot_start_utc, s.tz || tz) : labelFromTimestamp(s.slot_start));

export const slotEndLabel = (s: SlotUtc, tz: string) =>
  (s.slot_end_utc ? labelFromInstant(s.slot_end_utc, s.tz || tz) : labelFromTimestamp(s.slot_end));

/**
 * Clave para cruzar disponibilidad con ocupación. Con instantes es el propio
 * instante normalizado, así que no depende de cómo serialice cada RPC.
 */
export const slotKeyOf = (s: { slot_start?: string; slot_start_utc?: string | null }) => {
  if (s.slot_start_utc) {
    const d = new Date(s.slot_start_utc);
    return isNaN(d.getTime()) ? String(s.slot_start_utc) : d.toISOString();
  }
  return minuteKey(s.slot_start);
};

/**
 * ¿El cupo todavía no pasó? Con el instante es una comparación de verdad y no
 * necesita saber si el día es hoy; sin él, la de siempre contra el reloj de la
 * sucursal.
 */
export const slotIsFuture = (s: SlotUtc, ymd: string, tz: string, at?: Date) => {
  if (s.slot_start_utc) {
    const d = new Date(s.slot_start_utc);
    return !isNaN(d.getTime()) && d.getTime() > (at ?? new Date()).getTime();
  }
  return isFutureSlotSameDay(ymd, s.slot_start, tz, at);
};

/* ========================= avisos de agenda (outbox de la base) ========================= */
/**
 * ¿La sucursal ya le avisa por correo al cliente final cuando agenda?
 *
 * Si sí, la base encola ella misma la confirmación (outbox
 * `appointment_communications`, con botones Reagendar/Cancelar) y la web NO
 * tiene que mandar la suya: si no, el cliente recibe dos correos por la misma
 * cita. Los correos a la automotora y al vendedor no cambian — la outbox no
 * los reemplaza.
 *
 * Regla copiada de `public.appointment_comms_norm(jsonb)` (migración
 * `20260831120000_agenda_comunicaciones.sql`), caso `confirmation` + canal email:
 *   confirm_enabled = COALESCE(confirmation.enabled, confirm_enabled, false)
 *   confirm_email   = COALESCE(confirmation.channels.email, channel_email, false)
 *
 * Es decir: gana la forma anidada `{confirmation:{enabled,channels:{email}}}`;
 * si falta, vale la forma plana `{confirm_enabled, channel_email}` (los canales
 * planos son el default de TODOS los tipos); si tampoco está, false. Todo lo
 * que no se entienda (null, no-objeto, clave con tipo raro) se lee como
 * APAGADO, nunca al revés — igual que la base. Cambiar la regla acá sin
 * cambiarla allá vuelve a duplicar (o a perder) el correo.
 */
const esObjeto = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v);

/**
 * `(jsonb ->> clave)::boolean` de Postgres: un boolean JSON pasa tal cual y un
 * string se interpreta como boolean de Postgres (`true/false/t/f/yes/no/on/off/1/0`,
 * sin distinguir mayúsculas). Falta, null u otra cosa → null (= "no dijo").
 */
const pgBool = (v: unknown): boolean | null => {
  if (typeof v === 'boolean') return v;
  if (typeof v === 'number') return v === 1 ? true : v === 0 ? false : null;
  if (typeof v !== 'string') return null;
  const s = v.trim().toLowerCase();
  if (['true', 't', 'yes', 'y', 'on', '1'].includes(s)) return true;
  if (['false', 'f', 'no', 'n', 'off', '0'].includes(s)) return false;
  return null;
};

export function avisoPorCorreoActivo(appointmentComms: unknown): boolean {
  const c = esObjeto(appointmentComms) ? appointmentComms : {};
  const conf = esObjeto(c.confirmation) ? c.confirmation : {};
  const canales = esObjeto(conf.channels) ? conf.channels : {};

  const enabled = pgBool(conf.enabled) ?? pgBool(c.confirm_enabled) ?? false;
  const email = pgBool(canales.email) ?? pgBool(c.channel_email) ?? false;
  return enabled && email;
}

/* ══════════════════════════════════════════════════════════════════════════
 * La cita que queda esperando que la automotora la acepte (ticket #228)
 * ══════════════════════════════════════════════════════════════════════════
 *
 * `fn_appointment_book` ya no escribe siempre `confirmed`: cuando la sucursal
 * encendió `web_appointments_require_approval`, la cita que entra por la web
 * nace `pending` y la automotora tiene que aceptarla. El cupo queda reservado
 * mientras espera, así que para el visitante la hora SÍ está tomada — lo que
 * falta es la confirmación.
 *
 * El sitio necesita saberlo para no decirle "¡Cita agendada!" a quien todavía
 * espera respuesta, y por eso la RPC devuelve también el estado.
 */

/**
 * La fila que devuelven `fn_appointment_book` y `fn_appointment_book_utc`.
 *
 * `status` es OPCIONAL a propósito: lo agregó la migración del #228, y mientras
 * ésta no esté aplicada la RPC devuelve dos columnas. Con `status` ausente
 * `quedoPorAceptar` da `false` y el sitio se comporta exactamente como antes
 * —que además es lo correcto: sin la migración ninguna cita nace `pending`—.
 * Así el sitio se puede desplegar antes, después o a la vez que la base sin que
 * haya un instante en que diga algo falso.
 */
export interface BookedRow {
  appointment_id: number;
  cancel_token: string;
  status?: string | null;
}

/** ¿La reserva quedó esperando que la automotora la acepte? */
export const quedoPorAceptar = (row?: BookedRow | null): boolean =>
  row?.status === 'pending';
